import { useEffect, useState } from 'react';
import Spinner from 'react-spinkit';
import Table from '../Tables/Table';
import styles from './PopularGames.module.scss';
import generalStyles from './SchoolReport.module.scss';
import associationData from '../../ClassroomDashboard/Associations.json';

interface GameEntry {
  completionPercent: number;
  displayName: string;
  id: string;
  timeSpent: number;
  type: string;
}

interface Props {
  gameProgressMap: Record<string, unknown>;
  gameProgressMapReady: boolean;
}

const associations = (associationData as { associations: Record<string, { displayName: string }> }).associations;

const getDisplayName = (activityId: string): string => {
  const entry = associations[activityId];
  if (entry) return entry.displayName;
  const parts = activityId.match(/([A-Z]?[a-z]+)/g);
  if (parts) return parts.map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  return activityId;
};

const PopularGames = ({ gameProgressMap, gameProgressMapReady }: Props): JSX.Element => {
  const [data, setData] = useState<{ name: string; count: number }[]>([]);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (gameProgressMapReady) {
      buildData();
    }
  }, [gameProgressMap, gameProgressMapReady]);

  function buildData() {
    const gameCounts: Record<string, number> = {};

    Object.values(gameProgressMap).forEach((progress) => {
      if (!Array.isArray(progress)) return;
      const entries = progress as GameEntry[];
      entries.forEach((entry) => {
        if (!entry) return;
        const isCounted =
          (entry.type === 'quest' && entry.completionPercent > 0) ||
          (['forever', 'daily', 'finite'].includes(entry.type) && entry.timeSpent > 0);
        if (isCounted) {
          const name = getDisplayName(entry.displayName || entry.id || '');
          gameCounts[name] = (gameCounts[name] || 0) + 1;
        }
      });
    });

    const result = Object.entries(gameCounts)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);

    setData(result);
    setReady(true);
  }

  return (
    <div className={styles.popularGames}>
      <h4 className={generalStyles.tableHeader}>Most Popular Games</h4>
      <Table tableHeader={[{ title: 'Game' }, { title: 'Plays' }]}>
        {getContent()}
      </Table>
    </div>
  );

  function getContent() {
    if (!ready) {
      return (
        <tr>
          <td className={generalStyles.tdForProgressMessage}>
            <div className={generalStyles.progressMessage}>
              Loading
              <Spinner name='line-scale' />
            </div>
          </td>
        </tr>
      );
    }

    if (data.length === 0) {
      return (
        <tr>
          <td className={generalStyles.tdForProgressMessage}>
            <div className={generalStyles.progressMessage}>No data available</div>
          </td>
        </tr>
      );
    }

    return (
      <>
        {data.map((d) => (
          <tr key={d.name}>
            <td>{d.name}</td>
            <td>{d.count}</td>
          </tr>
        ))}
      </>
    );
  }
};

export default PopularGames;
